import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusChip, cad, day, errMsg, shortId } from "@/components/sponsored/format";
import { QUEUE_CHANGED } from "@/components/admin/sponsored/useRefundQueue";

const OPEN = ["REQUESTED", "FAILED"];

function Decide({ req, mode, onClose, onDone }) {
  const [amount, setAmount] = useState(req.amount_cad);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const ok = mode === "reject" || (n > 0 && n <= Number(req.amount_cad) && /^\d+(\.\d{1,2})?$/.test(amount));
  const go = async () => {
    setBusy(true);
    try {
      const path = `/admin/sponsored-cashback/refund-requests/${req.id}/${mode}`;
      const r = await api.post(path, mode === "approve" ? { amount_cad: amount } : { note });
      toast[r.data.status === "FAILED" ? "error" : "success"](mode === "reject" ? "Refund declined" : r.data.status === "FAILED" ? "Stripe refused part of the refund — see errors" : `Refunded ${cad(r.data.refunded_minor / 100)} to the card`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "That didn't work"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`admin-refund-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{mode === "approve" ? "Approve refund to card" : "Decline refund"}</DialogTitle>
          <DialogDescription>{req.store_name} asked for {cad(req.amount_cad)}. {mode === "approve" ? "Approve the full amount or less. Anything not refunded goes back to their budget." : "The held amount goes back to their budget."}</DialogDescription>
        </DialogHeader>
        {mode === "approve" ? (
          <label className="block space-y-1 text-sm">Amount to refund (CA$)
            <input className="pf-input w-full px-3 py-2 text-sm" inputMode="decimal" aria-invalid={!ok} value={amount} onChange={(e) => setAmount(e.target.value.trim())} data-testid="admin-refund-amount-input" />
          </label>
        ) : (
          <textarea className="pf-input w-full min-h-[80px] px-3 py-2 text-sm" aria-label="Reason shown to the merchant" maxLength={500} placeholder="Reason shown to the merchant" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-refund-note-input" />
        )}
        <DialogFooter>
          <button onClick={go} disabled={!ok || busy} className="pf-btn-primary px-5 py-2 rounded-full text-sm disabled:opacity-50" data-testid={`admin-refund-${mode}-confirm`}>
            {busy ? "Working…" : mode === "approve" ? `Refund ${ok ? cad(n) : ""}` : "Decline"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminScRefunds({ sa, onChange }) {
  const [rows, setRows] = useState(null);
  const [act, setAct] = useState(null);
  const load = useCallback(() => api.get("/admin/sponsored-cashback/refund-requests").then((r) => setRows(r.data.rows)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <p className="text-sm text-slate-500" data-testid="admin-refunds-loading">Loading…</p>;
  if (!rows.length) return <p className="text-sm text-slate-400 italic" data-testid="admin-refunds-empty">No refund requests yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-sc-refunds">
      {!sa && <p className="text-xs text-amber-700">Only a super admin can approve or decline refunds.</p>}
      {rows.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white/70 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-refund-row-${r.id}`}>
          <div className="text-sm">
            <div className="font-semibold text-slate-900">{r.store_name || r.store_id} · {cad(r.amount_cad)}</div>
            <div className="text-xs text-slate-500">Requested {day(r.requested_at)}{r.status === "REFUNDED" ? ` · ${cad(r.refunded_cad)} refunded to card${r.released_minor ? ` · ${cad(r.released_minor / 100)} back to budget` : ""}` : ""}</div>
            {(r.refunds || []).map((x) => <div key={x.stripe_refund_id} className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2" data-testid={`admin-refund-stripe-${x.stripe_refund_id}`}><span className="font-mono" title={x.stripe_refund_id}>{shortId(x.stripe_refund_id, 14)}</span> · {cad(x.amount_minor / 100)} <StatusChip status={x.status} /></div>)}
            {(r.errors || []).map((x, i) => <div key={i} className="text-[11px] text-rose-700">{x.error}: {x.message}</div>)}
            {r.decision_note && <div className="text-xs text-slate-600">Note: {r.decision_note}</div>}
          </div>
          <div className="flex items-center gap-2">
            <StatusChip status={r.status} testid={`admin-refund-status-${r.id}`} />
            {sa && OPEN.includes(r.status) && <>
              <button onClick={() => setAct({ req: r, mode: "approve" })} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs" data-testid={`admin-refund-approve-${r.id}`}>Approve</button>
              <button onClick={() => setAct({ req: r, mode: "reject" })} className="pf-btn-glass px-4 py-1.5 rounded-full text-xs font-semibold" data-testid={`admin-refund-reject-${r.id}`}>Decline</button>
            </>}
          </div>
        </div>
      ))}
      {act && <Decide {...act} onClose={() => setAct(null)} onDone={() => { load(); onChange(); window.dispatchEvent(new Event(QUEUE_CHANGED)); }} />}
    </div>
  );
}
